import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { env } from './env';

const config = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

// Eager: firebase/app + firebase/auth only (~35KB gzip). Auth must be
// available synchronously at boot for AuthProvider's onAuthStateChanged.
//
// firebase/firestore (~155KB gzip) is NOT initialized here — see
// './firestoreSdk' for the lazy Firestore facade. Splitting these keeps
// Firestore out of the initial modulepreloaded payload; nothing on the
// pre-auth path (RequireAuth, /sign-in, /verify, /v/:token) touches
// Firestore, and the persistent-cache init still runs, just deferred until
// the first Firestore-backed hook actually subscribes/writes.
export const app: FirebaseApp = getApps().length === 0 ? initializeApp(config) : getApp();

export const auth: Auth = getAuth(app);

if (env.VITE_USE_FIREBASE_EMULATOR) {
  // Spec 004 e2e plumbing — see tests/e2e/* and firebase.json.
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });

  // Expose a sign-in helper to Playwright fixtures. `page.evaluate` runs in
  // the browser context where bare module specifiers like 'firebase/auth'
  // don't resolve, so the fixture can't dynamically import the SDK itself —
  // it calls this helper instead. Gated on the emulator flag so prod has
  // zero exposure to test affordances.
  void import('firebase/auth').then(({ signInWithEmailAndPassword }) => {
    (
      globalThis as unknown as {
        __ggTestSignIn?: (email: string, password: string) => Promise<unknown>;
      }
    ).__ggTestSignIn = (email, password) => signInWithEmailAndPassword(auth, email, password);
  });

  // Spec 007 e2e — expose the authenticated UID so Playwright specs can write
  // TransferDocs with the correct outgoing_uid (the firestore.rules read check
  // requires `request.auth.uid == resource.data.outgoing_uid`).
  Object.defineProperty(globalThis, '__ggCurrentUserUid', {
    configurable: true,
    get: () => auth.currentUser?.uid ?? null,
  });
}
